import React from 'react';
import cn from 'classnames';

import { DEFAULT_STYLES } from '../../constants';
import { getMobileListHeightStyle } from '../../utils/viewportHeightUtils';
import { ComboBoxOptionsListProps } from '../../types/comboBoxComponentTypes';

export const ComboBoxOptionsList: React.FC<ComboBoxOptionsListProps> = ({
  variant,
  filteredOptions,
  value,
  highlightedIndex,
  listBoxRef,
  mobileListRef,
  inputId,
  label,
  optionClassName,
  maxHeight = 344,
  shouldShowStickyButton = false,
  renderOption,
  getOptionLabel,
  getOptionValue,
  onSelect,
  onHighlight,
  emptyState
}) => {
  if (variant === 'mobile') {
    const listHeightStyle = getMobileListHeightStyle(120);

    return (
      <div
        ref={mobileListRef}
        className="overflow-y-auto flex-1 p-2 px-3 box-border overscroll-contain touch-pan-y"
        style={listHeightStyle}
      >
        {filteredOptions.length > 0
          ? filteredOptions.map((option) => (
            <div
              key={getOptionValue(option)}
              onClick={() => {
                if (!option.disabled) {
                  onSelect(getOptionValue(option));
                }
              }}
              role="option"
              aria-selected={getOptionValue(option) === value}
              aria-disabled={option.disabled}
              className={cn(
                DEFAULT_STYLES.combobox__bottomSheet__option,
                getOptionValue(option) === value &&
                DEFAULT_STYLES.combobox__bottomSheet__optionSelected,
                option.disabled &&
                DEFAULT_STYLES.combobox__bottomSheet__optionDisabled,
                optionClassName
              )}
            >
              <div className="flex items-center justify-between w-full">
                <span className="flex-1 break-words whitespace-normal">
                  {renderOption
                    ? renderOption(option)
                    : getOptionLabel(option)}
                </span>
              </div>
            </div>
          ))
          : emptyState}
      </div>
    );
  }

  return (
    <ul
      ref={listBoxRef}
      role="listbox"
      aria-labelledby={label ? `${inputId}-label` : undefined}
      className="py-2 px-2 overflow-y-auto outline-none flex-1 box-border"
      style={{
        maxHeight: shouldShowStickyButton
          ? `${maxHeight - 80}px`
          : `${maxHeight}px`
      }}
    >
      {filteredOptions.length > 0
        ? filteredOptions.map((option, index) => (
          <li
            key={getOptionValue(option)}
            role="option"
            aria-selected={getOptionValue(option) === value}
            aria-disabled={option.disabled}
            onClick={() => {
              if (!option.disabled) {
                onSelect(getOptionValue(option));
              }
            }}
            onMouseEnter={() => onHighlight(index)}
            className={cn(
              DEFAULT_STYLES.dropdown__option,
              getOptionValue(option) === value &&
              DEFAULT_STYLES.dropdown__optionSelected,
              option.disabled && DEFAULT_STYLES.dropdown__optionDisabled,
              highlightedIndex === index &&
              !option.disabled &&
              'bg-bottomSheetOptionBg',
              optionClassName
            )}
          >
            <div className="flex items-center justify-between w-full">
              <span className="flex-1 break-words whitespace-normal">
                {renderOption
                  ? renderOption(option)
                  : getOptionLabel(option)}
              </span>
            </div>
          </li>
        ))
        : emptyState}
    </ul>
  );
};

ComboBoxOptionsList.displayName = 'ComboBoxOptionsList';
